import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { CropProps, CropTableProps } from 'utils/types';
import { FieldProps } from 'features/Croptimizer/types';
import { getClean } from 'hooks/useMaskedInput';
import {
  cmToFineDry,
  generateId, numericValues, unMaskInput, unMaskInputValue,
} from 'utils/helpers';
import { useCroptimizer } from 'features/Croptimizer/providers/CroptimizerContext';
import {
  MONPShelper, npkGainsCropHelper, yieldPercentHelper, yieldPriceHelper,
} from 'features/Croptimizer/utils/calcHelperBoard';
import useCroptimizerMineralization from 'api/hooks/useCroptimizerMineralization';

export const useGenerateCrop = () => {
  const { t } = useTranslation();
  const { data: mineralizationList } = useCroptimizerMineralization();

  const { boardStatusValues, boardSoilValues, mineralizationValues, fertilizationValues } = useCroptimizer();

  const getMonps = useCallback(
    (object: Partial<FieldProps['object']>) => MONPShelper(
      cmToFineDry(Number(unMaskInput(boardSoilValues?.fineDrySoil ?? '20'))),
      Number(numericValues(getClean(object?.omI ?? '3'))) / 100,
      Number(getClean(object?.targetphI ?? '6,3')),
      Number(getClean(object?.currentphI ?? '5,8')) -
      Number(mineralizationValues?.annualDecrease || '0.1'),
      Number(numericValues(mineralizationList?.[0]?.NRate ?? '')) / 100,
      Number(numericValues(mineralizationList?.[0]?.P2O5Rate ?? '')) / 100,
      Number(numericValues(mineralizationList?.[0]?.SO3Rate ?? '')) / 100,
      Number(getClean(object?.nI ?? fertilizationValues?.nprice ?? '0')),
      Number(getClean(object?.pI ?? fertilizationValues?.pprice ?? '0')),
      Number(getClean(object?.sI ?? mineralizationValues?.sPrice ?? '0')),
    ),
    [boardSoilValues, mineralizationValues, mineralizationList],
  );

  const phAvg = useMemo(
    () => Number(mineralizationValues?.annualDecrease || '0.1'),
    [mineralizationValues?.annualDecrease],
  );

  const getDataWithAvg = (
    index: number,
    currentWithAvg = +unMaskInput(boardStatusValues?.currentPh || '5,8'),
    targetWithAvg = +unMaskInput(boardStatusValues?.targetPh || '6,3'),
  ): {
    currentWithAvg: number;
    targetWithAvg: number;
  } => {
    if (index > 0) {
      return getDataWithAvg(
        --index,
        currentWithAvg - Number(phAvg),
        +(targetWithAvg - Number(phAvg)).toFixed(1),
      );
    }

    return {
      currentWithAvg: +(currentWithAvg - Number(phAvg)).toFixed(1),
      targetWithAvg: targetWithAvg,
    };
  };

  const generateCrop = (
    value: CropProps,
    object: Partial<FieldProps['object']>,
    id?: number,
    cropData?: Pick<CropTableProps, 'yield' | 'price' | 'n' | 'p' | 'k'>,
    index?: number,
  ): CropTableProps => {
    const cleantargetph = getClean(
      object?.targetphI ??
      (unMaskInputValue(boardStatusValues?.targetPh ?? '6,3') || '6,3'),
    );
    const cleancurrentph = getClean(
      object?.currentphI ??
      (unMaskInputValue(boardStatusValues?.currentPh ?? '5,8') || '5,8'),
    );

    // eslint-disable-next-line prefer-const
    let { currentWithAvg, targetWithAvg } = getDataWithAvg(
      index || 0,
      +cleancurrentph,
      +cleantargetph,
    );

    let monps = getMonps(object);

    if (cleantargetph === cleancurrentph) {
      targetWithAvg = +cleantargetph;
      monps = monps * ((index || 0) + 1);
    }

    const phIfNoLim = currentWithAvg;
    const yieldPercent = yieldPercentHelper(
      value.GroupYeildLosses,
      phIfNoLim,
      targetWithAvg,
    );

    const yieldPrice = yieldPriceHelper(
      cropData?.yield || value.PotentialYeild,
      cropData?.price || value.Price,
      Number(Number(yieldPercent) / 100),
    );

    const npkGains = npkGainsCropHelper(
      cropData?.n
        ? +unMaskInput(cropData.n)
        : +unMaskInput(fertilizationValues?.nvalue ?? '0'),
      cropData?.p
        ? +unMaskInput(cropData.p)
        : +unMaskInput(fertilizationValues?.pvalue ?? '0'),
      cropData?.k
        ? +unMaskInput(cropData.k)
        : +unMaskInput(fertilizationValues?.kvalue ?? '0'),
      Number(getClean(object?.nI ?? fertilizationValues?.nprice ?? '0')),
      Number(getClean(object?.pI ?? fertilizationValues?.pprice ?? '0')),
      Number(getClean(object?.kI ?? fertilizationValues?.kprice ?? '0')),
      phIfNoLim,
      targetWithAvg,
    );

    return {
      id: id || generateId(),
      name: value.Crops,
      yield: cropData?.yield || value.PotentialYeild,
      price: cropData?.price || value.Price,
      phIfNoLim,
      limiting:
        Number(cleancurrentph) < Number(cleantargetph)
          ? t('improving')
          : t('maintanence'),
      expectPh: String(targetWithAvg),
      yieldPercent,
      yieldPrice,
      n: cropData?.n || fertilizationValues?.nvalue || '0',
      p: cropData?.p || fertilizationValues?.pvalue || '0',
      k: cropData?.k || fertilizationValues?.kvalue || '0',
      npkGains: npkGains,
      monps,
      total: Math.round(Number(yieldPrice) + npkGains + monps),
    };
  };

  return {
    generateCrop,
  };
};